/**
 * IgamingScheduledBiWarehouseExportsEtlPage — iGaming warehouse ETL pipelines.
 *
 * Lists scheduled BI warehouse export pipelines (BigQuery / Snowflake / Redshift)
 * with destination, schedule, dataset, last run, and status. A "New Pipeline"
 * button opens a right-hand drawer with a warehouse-type radio that reveals a
 * conditional credentials form, plus dataset, table prefix, cron schedule, and
 * retention policy.
 *
 * Ported from the pre-nuke legacy exports history page; modernized to drop
 * legacy UI imports and the authedLayoutRoute parent, and to call the new
 * /api/v1/igaming-scheduled-bi-warehouse-exports-etl endpoint.
 */
import { useState, type FormEvent } from "react";
import {
	useIgamingScheduledBiWarehouseExportsEtlList,
	useIgamingScheduledBiWarehouseExportsEtlCreate,
	useIgamingScheduledBiWarehouseExportsEtlDelete,
} from "./hooks";
import type {
	IgamingScheduledBiWarehouseExportsEtlRow,
	PipelineStatus,
	WarehouseType,
} from "./api";

const CARD = "rounded-lg border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20";
const LABEL = "text-xs font-medium uppercase tracking-wide text-zinc-500";
const BTN_PRIMARY =
	"inline-flex items-center gap-1.5 rounded-md border border-sky-600 bg-sky-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"inline-flex items-center gap-1.5 rounded-md border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50";

const WAREHOUSE_OPTIONS: { value: WarehouseType; label: string }[] = [
	{ value: "bigquery", label: "BigQuery" },
	{ value: "snowflake", label: "Snowflake" },
	{ value: "redshift", label: "Redshift" },
];

const RETENTION_OPTIONS = [
	{ value: "7d", label: "7 days" },
	{ value: "30d", label: "30 days" },
	{ value: "90d", label: "90 days" },
	{ value: "365d", label: "1 year" },
	{ value: "forever", label: "Keep forever" },
];

const STATUS_STYLES: Record<PipelineStatus, string> = {
	idle: "border-zinc-200 bg-zinc-50 text-zinc-600",
	queued: "border-zinc-200 bg-zinc-50 text-zinc-600",
	running: "border-sky-200 bg-sky-50 text-sky-700",
	ready: "border-emerald-200 bg-emerald-50 text-emerald-700",
	error: "border-rose-200 bg-rose-50 text-rose-700",
};

function formatLastRun(ts?: number | null): string {
	if (!ts) return "never";
	const ms = ts < 1e12 ? ts * 1000 : ts;
	const diff = Date.now() - ms;
	const mins = Math.round(diff / 60000);
	if (mins < 1) return "just now";
	if (mins < 60) return `${mins}m ago`;
	const hrs = Math.round(mins / 60);
	if (hrs < 24) return `${hrs}h ago`;
	const days = Math.round(hrs / 24);
	return `${days}d ago`;
}

interface DrawerFormState {
	destination: string;
	warehouseType: WarehouseType;
	schedule: string;
	dataset: string;
	tablePrefix: string;
	retentionPolicy: string;
	bqProject: string;
	bqKeyJson: string;
	sfAccount: string;
	sfUser: string;
	sfPassword: string;
	rsHost: string;
	rsUser: string;
	rsPassword: string;
}

const DEFAULT_FORM: DrawerFormState = {
	destination: "",
	warehouseType: "bigquery",
	schedule: "0 3 * * *",
	dataset: "",
	tablePrefix: "bi_",
	retentionPolicy: "30d",
	bqProject: "",
	bqKeyJson: "",
	sfAccount: "",
	sfUser: "",
	sfPassword: "",
	rsHost: "",
	rsUser: "",
	rsPassword: "",
};

export function IgamingScheduledBiWarehouseExportsEtlPage() {
	const listQuery = useIgamingScheduledBiWarehouseExportsEtlList();
	const createMut = useIgamingScheduledBiWarehouseExportsEtlCreate();
	const deleteMut = useIgamingScheduledBiWarehouseExportsEtlDelete();

	const [drawerOpen, setDrawerOpen] = useState(false);
	const [form, setForm] = useState<DrawerFormState>(DEFAULT_FORM);

	const openDrawer = () => {
		setForm(DEFAULT_FORM);
		setDrawerOpen(true);
	};
	const closeDrawer = () => setDrawerOpen(false);

	const handleCreate = (e: FormEvent) => {
		e.preventDefault();
		if (!form.destination.trim() || !form.dataset.trim()) return;
		const credentials: Record<string, string> = {};
		if (form.warehouseType === "bigquery") {
			if (form.bqProject) credentials.project = form.bqProject;
			if (form.bqKeyJson) credentials.key_json = form.bqKeyJson;
		} else if (form.warehouseType === "snowflake") {
			if (form.sfAccount) credentials.account = form.sfAccount;
			if (form.sfUser) credentials.user = form.sfUser;
			if (form.sfPassword) credentials.password = form.sfPassword;
		} else if (form.warehouseType === "redshift") {
			if (form.rsHost) credentials.host = form.rsHost;
			if (form.rsUser) credentials.user = form.rsUser;
			if (form.rsPassword) credentials.password = form.rsPassword;
		}
		createMut.mutate(
			{
				destination: form.destination.trim(),
				warehouse_type: form.warehouseType,
				schedule: form.schedule.trim(),
				dataset: form.dataset.trim(),
				table_prefix: form.tablePrefix.trim() || undefined,
				retention_policy: form.retentionPolicy,
				credentials:
					Object.keys(credentials).length > 0 ? credentials : undefined,
			},
			{
				onSuccess: () => {
					setForm(DEFAULT_FORM);
					setDrawerOpen(false);
				},
			},
		);
	};

	const items: IgamingScheduledBiWarehouseExportsEtlRow[] =
		listQuery.data?.items ?? [];

	return (
		<main
			data-testid="igaming-scheduled-bi-warehouse-exports-etl-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-6 py-10"
		>
			<header className="flex items-start justify-between gap-4">
				<div>
					<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
						iGaming &middot; BI warehouse
					</p>
					<h1 className="mt-1 bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent">
						Scheduled warehouse exports
					</h1>
					<p className="mt-1 max-w-2xl text-sm text-zinc-500">
						Configure recurring ETL pipelines that ship analytics datasets to
						your BigQuery, Snowflake, or Redshift warehouse on a cron schedule.
					</p>
				</div>
				<button
					type="button"
					className={BTN_PRIMARY}
					onClick={openDrawer}
					data-testid="igaming-scheduled-bi-warehouse-exports-etl-new"
				>
					New pipeline
				</button>
			</header>

			<section
				className={CARD}
				aria-label="Warehouse pipelines"
				data-testid="igaming-scheduled-bi-warehouse-exports-etl-section"
			>
				{listQuery.isLoading ? (
					<p
						className="text-sm text-zinc-500"
						data-testid="igaming-scheduled-bi-warehouse-exports-etl-loading"
					>
						Loading pipelines.
					</p>
				) : listQuery.error ? (
					<p className="text-sm text-rose-600" role="alert">
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<div
						className="flex flex-col items-center justify-center rounded-md border border-dashed border-zinc-200 bg-white px-6 py-14 text-center"
						data-testid="igaming-scheduled-bi-warehouse-exports-etl-empty"
					>
						<div
							aria-hidden
							className="mb-4 h-14 w-14 rounded-full bg-gradient-to-br from-emerald-100 to-sky-100"
						/>
						<h3 className="text-base font-semibold text-zinc-900">
							No pipelines yet
						</h3>
						<p className="mt-1 max-w-md text-sm text-zinc-500">
							Create a pipeline to stream iGaming analytics into your data
							warehouse. Each run materialises a versioned dataset with your
							table prefix.
						</p>
						<button
							type="button"
							className={`${BTN_PRIMARY} mt-4`}
							onClick={openDrawer}
						>
							New pipeline
						</button>
					</div>
				) : (
					<div className="overflow-hidden rounded-md border border-zinc-200">
						<table
							className="w-full border-collapse text-sm"
							data-testid="igaming-scheduled-bi-warehouse-exports-etl-list"
						>
							<thead className="bg-zinc-50 text-left text-xs font-medium uppercase tracking-wide text-zinc-500">
								<tr>
									<th className="px-4 py-2.5">Destination</th>
									<th className="px-4 py-2.5">Warehouse</th>
									<th className="px-4 py-2.5">Schedule</th>
									<th className="px-4 py-2.5">Dataset</th>
									<th className="px-4 py-2.5">Last run</th>
									<th className="px-4 py-2.5">Status</th>
									<th className="px-4 py-2.5 text-right" />
								</tr>
							</thead>
							<tbody>
								{items.map((row) => (
									<tr
										key={row.id}
										className="border-t border-zinc-100"
										data-testid={`igaming-scheduled-bi-warehouse-exports-etl-row-${row.id}`}
									>
										<td className="px-4 py-2.5 font-medium text-zinc-900">
											{row.destination}
										</td>
										<td className="px-4 py-2.5 text-zinc-600">
											{WAREHOUSE_OPTIONS.find(
												(o) => o.value === row.warehouse_type,
											)?.label ?? row.warehouse_type}
										</td>
										<td className="px-4 py-2.5 font-mono text-xs text-zinc-600">
											{row.schedule}
										</td>
										<td className="px-4 py-2.5 text-zinc-600">
											{row.dataset}
											{row.table_prefix ? (
												<span className="ml-1 text-xs text-zinc-400">
													({row.table_prefix})
												</span>
											) : null}
										</td>
										<td className="px-4 py-2.5 text-zinc-500">
											{formatLastRun(row.last_run_at)}
										</td>
										<td className="px-4 py-2.5">
											<span
												className={`inline-flex rounded-md border px-2 py-0.5 text-[11px] font-medium ${STATUS_STYLES[row.status] ?? STATUS_STYLES.idle}`}
											>
												{row.status}
											</span>
										</td>
										<td className="px-4 py-2.5 text-right">
											<button
												type="button"
												className={BTN_GHOST}
												onClick={() => deleteMut.mutate(row.id)}
												disabled={deleteMut.isPending}
												data-testid={`igaming-scheduled-bi-warehouse-exports-etl-delete-${row.id}`}
											>
												Delete
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>

			{drawerOpen ? (
				<div
					className="fixed inset-0 z-40 flex"
					data-testid="igaming-scheduled-bi-warehouse-exports-etl-drawer"
				>
					<button
						type="button"
						aria-label="Close drawer"
						className="flex-1 bg-zinc-900/30"
						onClick={closeDrawer}
					/>
					<aside className="flex h-full w-full max-w-md flex-col overflow-y-auto border-l border-zinc-200 bg-white p-6 shadow-xl">
						<header className="flex items-start justify-between">
							<div>
								<p className={LABEL}>New pipeline</p>
								<h2 className="mt-1 text-lg font-semibold text-zinc-900">
									Warehouse export
								</h2>
							</div>
							<button
								type="button"
								className={BTN_GHOST}
								onClick={closeDrawer}
							>
								Close
							</button>
						</header>
						<form
							onSubmit={handleCreate}
							className="mt-5 flex flex-col gap-4"
							data-testid="igaming-scheduled-bi-warehouse-exports-etl-create-form"
						>
							<div className="flex flex-col gap-1.5">
								<label className={LABEL} htmlFor="dest">
									Destination
								</label>
								<input
									id="dest"
									className={INPUT}
									placeholder="prod-warehouse-01"
									value={form.destination}
									onChange={(e) =>
										setForm((f) => ({ ...f, destination: e.currentTarget.value }))
									}
									data-testid="igaming-scheduled-bi-warehouse-exports-etl-destination"
								/>
							</div>

							<div className="flex flex-col gap-2">
								<span className={LABEL}>Warehouse type</span>
								<div
									className="grid grid-cols-3 gap-2"
									role="radiogroup"
									aria-label="Warehouse type"
								>
									{WAREHOUSE_OPTIONS.map((opt) => {
										const active = form.warehouseType === opt.value;
										return (
											<label
												key={opt.value}
												className={`flex cursor-pointer items-center justify-center rounded-md border px-3 py-2 text-sm font-medium ${active ? "border-sky-500 bg-sky-50 text-sky-700" : "border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50"}`}
											>
												<input
													type="radio"
													name="warehouse-type"
													className="sr-only"
													value={opt.value}
													checked={active}
													onChange={() =>
														setForm((f) => ({
															...f,
															warehouseType: opt.value,
														}))
													}
													data-testid={`igaming-scheduled-bi-warehouse-exports-etl-warehouse-${opt.value}`}
												/>
												{opt.label}
											</label>
										);
									})}
								</div>
							</div>

							{form.warehouseType === "bigquery" ? (
								<div className="flex flex-col gap-3 rounded-md border border-zinc-200 bg-zinc-50 p-3">
									<div className="flex flex-col gap-1.5">
										<label className={LABEL} htmlFor="bq-project">
											GCP project
										</label>
										<input
											id="bq-project"
											className={INPUT}
											placeholder="my-gcp-project"
											value={form.bqProject}
											onChange={(e) =>
												setForm((f) => ({
													...f,
													bqProject: e.currentTarget.value,
												}))
											}
										/>
									</div>
									<div className="flex flex-col gap-1.5">
										<label className={LABEL} htmlFor="bq-key">
											Service account key (JSON)
										</label>
										<textarea
											id="bq-key"
											className={`${INPUT} font-mono`}
											rows={3}
											placeholder='{"type":"service_account", ...}'
											value={form.bqKeyJson}
											onChange={(e) =>
												setForm((f) => ({
													...f,
													bqKeyJson: e.currentTarget.value,
												}))
											}
										/>
									</div>
								</div>
							) : null}

							{form.warehouseType === "snowflake" ? (
								<div className="flex flex-col gap-3 rounded-md border border-zinc-200 bg-zinc-50 p-3">
									<div className="flex flex-col gap-1.5">
										<label className={LABEL} htmlFor="sf-account">
											Account identifier
										</label>
										<input
											id="sf-account"
											className={INPUT}
											placeholder="xy12345.us-east-1"
											value={form.sfAccount}
											onChange={(e) =>
												setForm((f) => ({
													...f,
													sfAccount: e.currentTarget.value,
												}))
											}
										/>
									</div>
									<div className="flex flex-col gap-1.5">
										<label className={LABEL} htmlFor="sf-user">
											User
										</label>
										<input
											id="sf-user"
											className={INPUT}
											value={form.sfUser}
											onChange={(e) =>
												setForm((f) => ({ ...f, sfUser: e.currentTarget.value }))
											}
										/>
									</div>
									<div className="flex flex-col gap-1.5">
										<label className={LABEL} htmlFor="sf-pass">
											Password
										</label>
										<input
											id="sf-pass"
											type="password"
											className={INPUT}
											value={form.sfPassword}
											onChange={(e) =>
												setForm((f) => ({
													...f,
													sfPassword: e.currentTarget.value,
												}))
											}
										/>
									</div>
								</div>
							) : null}

							{form.warehouseType === "redshift" ? (
								<div className="flex flex-col gap-3 rounded-md border border-zinc-200 bg-zinc-50 p-3">
									<div className="flex flex-col gap-1.5">
										<label className={LABEL} htmlFor="rs-host">
											Host
										</label>
										<input
											id="rs-host"
											className={INPUT}
											placeholder="cluster.xyz.us-west-2.redshift.amazonaws.com"
											value={form.rsHost}
											onChange={(e) =>
												setForm((f) => ({ ...f, rsHost: e.currentTarget.value }))
											}
										/>
									</div>
									<div className="flex flex-col gap-1.5">
										<label className={LABEL} htmlFor="rs-user">
											User
										</label>
										<input
											id="rs-user"
											className={INPUT}
											value={form.rsUser}
											onChange={(e) =>
												setForm((f) => ({ ...f, rsUser: e.currentTarget.value }))
											}
										/>
									</div>
									<div className="flex flex-col gap-1.5">
										<label className={LABEL} htmlFor="rs-pass">
											Password
										</label>
										<input
											id="rs-pass"
											type="password"
											className={INPUT}
											value={form.rsPassword}
											onChange={(e) =>
												setForm((f) => ({
													...f,
													rsPassword: e.currentTarget.value,
												}))
											}
										/>
									</div>
								</div>
							) : null}

							<div className="flex flex-col gap-1.5">
								<label className={LABEL} htmlFor="dataset">
									Dataset
								</label>
								<input
									id="dataset"
									className={INPUT}
									placeholder="analytics"
									value={form.dataset}
									onChange={(e) =>
										setForm((f) => ({ ...f, dataset: e.currentTarget.value }))
									}
									data-testid="igaming-scheduled-bi-warehouse-exports-etl-dataset"
								/>
							</div>

							<div className="flex flex-col gap-1.5">
								<label className={LABEL} htmlFor="table-prefix">
									Table prefix
								</label>
								<input
									id="table-prefix"
									className={INPUT}
									placeholder="bi_"
									value={form.tablePrefix}
									onChange={(e) =>
										setForm((f) => ({
											...f,
											tablePrefix: e.currentTarget.value,
										}))
									}
								/>
							</div>

							<div className="flex flex-col gap-1.5">
								<label className={LABEL} htmlFor="schedule">
									Schedule (cron)
								</label>
								<input
									id="schedule"
									className={`${INPUT} font-mono`}
									placeholder="0 3 * * *"
									value={form.schedule}
									onChange={(e) =>
										setForm((f) => ({ ...f, schedule: e.currentTarget.value }))
									}
								/>
							</div>

							<div className="flex flex-col gap-1.5">
								<label className={LABEL} htmlFor="retention">
									Retention policy
								</label>
								<select
									id="retention"
									className={INPUT}
									value={form.retentionPolicy}
									onChange={(e) =>
										setForm((f) => ({
											...f,
											retentionPolicy: e.currentTarget.value,
										}))
									}
								>
									{RETENTION_OPTIONS.map((opt) => (
										<option key={opt.value} value={opt.value}>
											{opt.label}
										</option>
									))}
								</select>
							</div>

							{createMut.error ? (
								<p className="text-xs text-rose-600" role="alert">
									{createMut.error.message}
								</p>
							) : null}

							<div className="flex items-center justify-end gap-2 border-t border-zinc-200 pt-4">
								<button
									type="button"
									className={BTN_GHOST}
									onClick={closeDrawer}
								>
									Cancel
								</button>
								<button
									type="submit"
									className={BTN_PRIMARY}
									disabled={
										createMut.isPending ||
										!form.destination.trim() ||
										!form.dataset.trim()
									}
									data-testid="igaming-scheduled-bi-warehouse-exports-etl-submit"
								>
									{createMut.isPending ? "Saving" : "Create pipeline"}
								</button>
							</div>
						</form>
					</aside>
				</div>
			) : null}
		</main>
	);
}

export default IgamingScheduledBiWarehouseExportsEtlPage;
